/**
 * Validate a post-login return path.
 *
 * This is XSS containment, not merely anti-phishing. Next's own docs are blunt:
 * "You must not send untrusted or unsanitized URLs to router.push or
 * router.replace... `javascript:` URLs sent to router.push or router.replace will
 * be executed in the context of your page." And `redirect()` on the server
 * "also accepts absolute URLs", so an unchecked value there is a server-issued
 * open redirect from our own domain.
 *
 * The destination space for booking is three routes, so this ALLOW-LISTS them
 * rather than trying to filter a hostile input. Anything else returns "/".
 *
 * Call it once on the server (app/login/page.tsx, which hands the client an
 * already-safe string) and again in the client immediately before navigating.
 */

/** /<location>/services/<slug>/book */
const BOOK_PATH = /^\/[a-z0-9-]{1,60}\/services\/[a-z0-9-]{1,80}\/book$/;
/** ?option=<uuid> — the only query string worth carrying through a login. */
const BOOK_QUERY =
  /^\?option=[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** /account/bookings/PM-A1B2C3 */
const BOOKING_DETAIL = /^\/account\/bookings\/PM-[0-9A-Z]{6}$/;

export const DEFAULT_NEXT = "/";

export function safeNext(raw: unknown): string {
  // Repeated params (?next=/a&next=//evil.com) arrive as an array; reject the
  // whole shape rather than gambling on which element wins.
  if (typeof raw !== "string" || !raw || raw.length > 512) return DEFAULT_NEXT;

  // Checked BEFORE parsing, so nothing depends on WHATWG backslash folding.
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) {
    return DEFAULT_NEXT;
  }

  let url: URL;
  try {
    // A base whose origin cannot be produced by any relative path. `new URL`
    // throws on some inputs, hence the catch — an uncaught TypeError in the
    // verify handler would strand the user on a form that just consumed their OTP.
    url = new URL(raw, "http://x.invalid");
  } catch {
    return DEFAULT_NEXT;
  }

  // Catches scheme tricks: "https:/evil.com", "http://local@evil.com",
  // "javascript:alert(1)" (origin "null"), and protocol-relative survivors.
  if (url.origin !== "http://x.invalid") return DEFAULT_NEXT;

  const path = url.pathname; // the hash is deliberately dropped
  if (path.startsWith("//")) return DEFAULT_NEXT; // re-check the OUTPUT, not just the input

  // Never bounce back into the login flow (that would loop) or into the admin,
  // which has its own separate gate and its own login.
  if (path === "/login" || path.startsWith("/login/")) return DEFAULT_NEXT;
  if (path === "/admin" || path.startsWith("/admin/")) return DEFAULT_NEXT;

  if (path === "/account/bookings" || BOOKING_DETAIL.test(path)) return path;

  if (BOOK_PATH.test(path)) {
    return url.search === "" || BOOK_QUERY.test(url.search) ? path + url.search : path;
  }

  return DEFAULT_NEXT;
}

/** Build a `?next=` query fragment for a login link. Empty for the default. */
export function nextParam(path: string): string {
  const safe = safeNext(path);
  return safe === DEFAULT_NEXT ? "" : `?next=${encodeURIComponent(safe)}`;
}
